import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import { getModulePermissions } from '../../utils/permissions';

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  username: '',
  roleId: '',
  photo: null,
  password: '',
  confirmPassword: '',
};

const sanitizePhone = (value) => (value || '').replace(/[^0-9]/g, '');

const formatStatus = (status) => {
  if (!status) return '-';
  const normalized = `${status}`;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const UserSettings = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [formAlert, setFormAlert] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [photoName, setPhotoName] = useState('');
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const photoInputRef = useRef(null);

  const permissions = useMemo(() => getModulePermissions('settings-user'), []);
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canManage = canCreate || canEdit;
  const isViewOnly = permissions.view && !canManage;

  const validateField = (field, value, allValues = formData) => {
    switch (field) {
      case 'name': {
        const trimmed = (value || '').trim();
        if (!trimmed) return 'Name is required.';
        if (trimmed.length < 2) return 'Name must be at least 2 characters.';
        return '';
      }
      case 'email': {
        const trimmed = (value || '').trim();
        if (!trimmed) return 'Email is required.';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) return 'Enter a valid email address.';
        return '';
      }
      case 'phone': {
        const digits = sanitizePhone(value);
        if (!digits) return 'Phone number is required.';
        if (digits.length < 10 || digits.length > 15)
          return 'Phone number must have between 10 and 15 digits.';
        return '';
      }
      case 'username': {
        const trimmed = (value || '').trim();
        if (!trimmed) return 'Username is required.';
        if (trimmed.length < 3) return 'Username must be at least 3 characters.';
        if (!/^[a-zA-Z0-9._-]+$/.test(trimmed))
          return 'Username can contain letters, numbers, dots, underscores and hyphens only.';
        return '';
      }
      case 'roleId': {
        if (!value) return 'Please select a role.';
        return '';
      }
      case 'password': {
        if (!value) return 'Password is required.';
        if (value.length < 6) return 'Password must be at least 6 characters.';
        return '';
      }
      case 'confirmPassword': {
        if (!value) return 'Please confirm the password.';
        if (value !== allValues.password) return 'Passwords do not match.';
        return '';
      }
      case 'photo':
        return '';
      default:
        return '';
    }
  };

  const validateForm = (values) => {
    const fieldsToCheck = [
      'name',
      'email',
      'phone',
      'username',
      'roleId',
      'password',
      'confirmPassword',
    ];
    const validationErrors = {};
    fieldsToCheck.forEach((field) => {
      const error = validateField(field, values[field], values);
      if (error) {
        validationErrors[field] = error;
      }
    });
    return validationErrors;
  };

  const resetPhoto = useCallback(() => {
    setSelectedPhotoFile(null);
    setPhotoPreviewUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return '';
    });
    setPhotoName('');
    if (photoInputRef.current) photoInputRef.current.value = '';
  }, []);

  const resetForm = () => {
    setFormData(initialFormState);
    setErrors({});
    setFormAlert({ type: '', text: '' });
    resetPhoto();
  };

  useEffect(() => () => {
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
  }, [photoPreviewUrl]);

  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    setRolesError('');
    try {
      const response = await apiRequest('/api/admin/roles', {}, navigate);
      setRoles(response.data || []);
    } catch (error) {
      setRolesError(error.message || 'Unable to load roles.');
    } finally {
      setLoadingRoles(false);
    }
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setUsersError('');
    try {
      const response = await apiRequest('/api/admin/settings/users', {}, navigate);
      setUsers(response.data || []);
    } catch (error) {
      setUsersError(error.message || 'Unable to load users.');
    } finally {
      setLoadingUsers(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, [fetchRoles, fetchUsers]);

  useEffect(() => {
    if (!formData.roleId) return;
    const exists = roles.some((item) => `${item.id}` === `${formData.roleId}`);
    if (!exists) {
      setFormData((prev) => ({ ...prev, roleId: '' }));
    }
  }, [roles, formData.roleId]);

  const handleInputChange = (field) => (event) => {
    let { value } = event.target;
    if (field === 'phone') {
      value = sanitizePhone(value);
    }

    const updated = { ...formData, [field]: value };
    setFormData(updated);

    setErrors((prev) => {
      const newErrors = { ...prev };
      const fieldError = validateField(field, value, updated);
      if (fieldError) {
        newErrors[field] = fieldError;
      } else {
        delete newErrors[field];
      }

      if (field === 'password') {
        const confirmError = validateField('confirmPassword', updated.confirmPassword, updated);
        if (confirmError) {
          newErrors.confirmPassword = confirmError;
        } else {
          delete newErrors.confirmPassword;
        }
      }

      return newErrors;
    });
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    resetPhoto();

    if (!file) {
      setErrors((prev) => {
        const { photo, ...rest } = prev;
        return rest;
      });
      return;
    }

    const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!acceptedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, photo: 'Photo must be a JPG, PNG, or WEBP image.' }));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, photo: 'Photo must be smaller than 2MB.' }));
      return;
    }

    setSelectedPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setPhotoName(file.name);
    setErrors((prev) => {
      const { photo, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canCreate) {
      setFormAlert({ type: 'error', text: 'You do not have permission to create users.' });
      return;
    }
    const trimmedValues = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      username: formData.username.trim(),
    };

    const validationErrors = validateForm(trimmedValues);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setFormAlert({ type: 'error', text: 'Please fix the highlighted fields.' });
      return;
    }

    setSubmitting(true);
    setFormAlert({ type: '', text: '' });

    const payload = new FormData();
    payload.append('name', trimmedValues.name);
    payload.append('email', trimmedValues.email.toLowerCase());
    payload.append('phone', sanitizePhone(trimmedValues.phone));
    payload.append('username', trimmedValues.username);
    payload.append('roleId', trimmedValues.roleId);
    payload.append('password', trimmedValues.password);
    payload.append('confirmPassword', trimmedValues.confirmPassword);
    if (selectedPhotoFile) payload.append('photo', selectedPhotoFile);

    try {
      const response = await apiRequest(
        '/api/admin/settings/users',
        { method: 'POST', body: payload },
        navigate
      );
      setFormAlert({ type: 'success', text: response.message || 'User created successfully.' });
      resetForm();
      fetchUsers();
    } catch (error) {
      setFormAlert({ type: 'error', text: error.message || 'Unable to save user.' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderAlert = () => {
    if (!formAlert.text) return null;
    const alertClass = formAlert.type === 'success' ? 'alert-success' : 'alert-danger';
    return (
      <div className={`alert ${alertClass}`} role="alert">
        {formAlert.text}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>User Settings</h4>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate('/settings')}
            type="button"
          >
            Back
          </button>
        </div>

        {isViewOnly && (
          <div className="alert alert-info" role="alert">
            You have view-only access. Create and edit actions are disabled.
          </div>
        )}

        <div className="row">
          {canCreate ? (
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-4">Add User</h5>

                  {renderAlert()}

                  {rolesError && (
                    <div className="alert alert-warning" role="alert">
                      {rolesError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} noValidate>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="text"
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            id="name"
                            placeholder="Enter Name"
                            value={formData.name}
                            onChange={handleInputChange('name')}
                          />
                          <label htmlFor="name">Enter Name *</label>
                        </div>
                        {errors.name && (
                          <div className="invalid-feedback d-block">{errors.name}</div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            id="email"
                            placeholder="Enter Mail ID"
                            value={formData.email}
                            onChange={handleInputChange('email')}
                          />
                          <label htmlFor="email">Enter Mail ID *</label>
                        </div>
                        {errors.email && (
                          <div className="invalid-feedback d-block">{errors.email}</div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="tel"
                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            id="phone"
                            placeholder="Enter Phone Number"
                            value={formData.phone}
                            onChange={handleInputChange('phone')}
                          />
                          <label htmlFor="phone">Enter Phone Number *</label>
                        </div>
                        {errors.phone && (
                          <div className="invalid-feedback d-block">{errors.phone}</div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="text"
                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                            id="username"
                            placeholder="Enter Username"
                            value={formData.username}
                            onChange={handleInputChange('username')}
                          />
                          <label htmlFor="username">Enter Username *</label>
                        </div>
                        {errors.username && (
                          <div className="invalid-feedback d-block">{errors.username}</div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <div className="form-floating">
                          <select
                            className={`form-select ${errors.roleId ? 'is-invalid' : ''}`}
                            id="role"
                            value={formData.roleId}
                            onChange={handleInputChange('roleId')}
                            disabled={loadingRoles || roles.length === 0}
                          >
                            <option value="">
                              {loadingRoles ? 'Loading roles...' : 'Select Role'}
                            </option>
                            {roles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name || role.description || `Role #${role.id}`}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="role">Select Role *</label>
                        </div>
                        {errors.roleId && (
                          <div className="invalid-feedback d-block">{errors.roleId}</div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="file"
                            className={`form-control ${errors.photo ? 'is-invalid' : ''}`}
                            id="photo"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handlePhotoChange}
                            ref={photoInputRef}
                          />
                          <label htmlFor="photo">Upload Photo</label>
                        </div>
                        {photoName && !errors.photo && (
                          <div className="form-text">Selected: {photoName}</div>
                        )}
                        {photoPreviewUrl && (
                          <img src={photoPreviewUrl} alt="Selected profile preview" className="img-thumbnail mt-2" style={{ width: 96, height: 96, objectFit: 'cover' }} />
                        )}
                        {errors.photo && (
                          <div className="invalid-feedback d-block">{errors.photo}</div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="password"
                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                            id="password"
                            placeholder="Create a Password"
                            value={formData.password}
                            onChange={handleInputChange('password')}
                          />
                          <label htmlFor="password">Create a Password *</label>
                        </div>
                        {errors.password && (
                          <div className="invalid-feedback d-block">{errors.password}</div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="password"
                            className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                            id="confirmPassword"
                            placeholder="Confirm Password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange('confirmPassword')}
                          />
                          <label htmlFor="confirmPassword">Confirm Password *</label>
                        </div>
                        {errors.confirmPassword && (
                          <div className="invalid-feedback d-block">{errors.confirmPassword}</div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4">
                      <button type="submit" className="btn btn-danger" disabled={submitting}>
                        {submitting ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            <div className="col-12 mb-4">
              {renderAlert()}
              <div className="alert alert-info mb-0" role="alert">
                You can view existing users, but you cannot create new users.
              </div>
            </div>
          )}
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">Existing Users</h5>

                {usersError && (
                  <div className="alert alert-danger" role="alert">
                    {usersError}
                  </div>
                )}

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Username</th>
                        <th>Phone</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingUsers ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            Loading users...
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No users found.
                          </td>
                        </tr>
                      ) : (
                        users.map((user, index) => (
                          <tr key={user.id}>
                            <td>{index + 1}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.username || '-'}</td>
                            <td>{user.phone || '-'}</td>
                            <td>{user.role?.name || '-'}</td>
                            <td>{formatStatus(user.status)}</td>
                            <td>
                              {canEdit && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-warning me-2"
                                  onClick={() => navigate(`/settings/users/${user.id}`)}
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                disabled
                                title="Delete functionality coming soon"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserSettings;
